import React from 'react';
import { useLocaleContext } from '../../../utils/LangContext';
import Link from 'next/link';
import styled from 'styled-components';
import { Fade } from 'react-awesome-reveal';
import BorderLink from '../../../components/shared/BorderedLink';
import { CONTACT_EMAIL } from '../../../utils/constants';
import { linkAttributes } from '../../../utils/linkAttributes';

const socialNav = [
  // { title: "facebook", link: "https://www.facebook.com/acueducto.studio/" },
  // { title: "tiktok", link: "https://www.tiktok.com/@acueducto.studio" },
  { title: 'Instagram', link: 'https://www.instagram.com/acueducto.studio/' },
  {
    title: 'YouTube',
    link: 'https://www.youtube.com/channel/UCuFV_fKt_ELREPwoAb5lprg',
  },
  {
    title: 'LinkedIn',
    link: 'https://www.linkedin.com/company/acueductostudio/',
  },
];
interface FooterNavProps {
  main: Array<{ title: string; link: string; as?: string }>;
  policies: Array<{ title: string; link: string; as?: string }>;
  navTitles: {
    main: string;
    community: string;
    policies: string;
    resources?: string;
    services?: string;
  };
  mailto: { subject: string; body: string };
  resources?: Array<{ title: string; link: string; as?: string }>;
  services?: Array<{ title: string; link: string; as?: string }>;
}

export default function FooterNav() {
  const context = useLocaleContext();
  const {
    main,
    policies,
    navTitles,
    mailto,
    resources,
    services,
  }: FooterNavProps = context.contact_footer.footer_nav;

  const year = new Date().getFullYear();
  const renderNavItems = (
    items: Array<{ title: string; link: string; as?: string }>,
    keyPrefix: string,
  ) =>
    items.map((item, index) => {
      const isExternal = item.link.startsWith('http');
      const attrs = linkAttributes(item.link);
      return (
        <Fade triggerOnce delay={200 + index * 50} key={keyPrefix + index}>
          <li>
            <Link
              href={item.link}
              as={isExternal ? undefined : item.as ? item.as : item.link}
              locale={isExternal ? undefined : context.lang}
              {...attrs}
            >
              {item.title}
            </Link>
          </li>
        </Fade>
      );
    });

  return (
    <Footer>
      <Navs>
        <NavList>
          <Fade triggerOnce>
            <span>{navTitles.main}</span>
          </Fade>
          <ul>{renderNavItems(main, 'itemMain')}</ul>
        </NavList>
        {services && (
          <NavList>
            <Fade triggerOnce>
              <span>{navTitles.services}</span>
            </Fade>
            <ul>{renderNavItems(services, 'itemService')}</ul>
          </NavList>
        )}
        {context.lang === 'es' && resources && (
          <NavList>
            <Fade triggerOnce>
              <span>{navTitles.resources}</span>
            </Fade>
            <ul>{renderNavItems(resources, 'itemResource')}</ul>
          </NavList>
        )}
        <NavList id="community">
          <Fade triggerOnce>
            <span>{navTitles.community}</span>
          </Fade>
          <ul>
            {socialNav.map((item, index) => (
              <Fade
                triggerOnce
                delay={200 + index * 50}
                key={'itemSocial' + index}
              >
                <li>
                  <Link href={item.link}>{item.title}</Link>
                </li>
              </Fade>
            ))}
          </ul>
        </NavList>
        <NavList $accentColor>
          <Fade triggerOnce>
            <span>{navTitles.policies}</span>
          </Fade>
          <ul>{renderNavItems(policies, 'itemPolicy')}</ul>
        </NavList>
      </Navs>
      <Colophon>
        <Fade triggerOnce>
          <p>© {year}, Acueducto</p>
          <a
            href={`mailto:${CONTACT_EMAIL}?subject=${mailto.subject}&body=${mailto.body}`}
          >
            {CONTACT_EMAIL}
          </a>
          {/* <a href="https://api.whatsapp.com/message/JNUILJPF23CSP1">
            <img src="/assets/img/studio/logos/whatsapp.svg" alt="WhatApp" />
            +52 55 2789 5399
          </a> */}
        </Fade>
      </Colophon>
    </Footer>
  );
}

const Footer = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  position: relative;
  width: 100%;
  padding: 5% 4%;
  background-color: ${(props) => props.theme.colors.background};
  @media (max-width: 600px) {
    padding-bottom: 13%;
  }
`;

const Navs = styled.div`
  grid-column: 2 / span 11;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));

  @media (max-width: 800px) {
    grid-column: 1 / span 12;
    grid-template-columns: 1fr 1fr;
    grid-row-gap: 20px;
  }
`;

const NavList = styled.div<{ $accentColor?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  &:hover {
    background-image: none !important;
  }
  ul {
    list-style: none;
    font-size: 1.6rem;
    color: ${(p) =>
      p.$accentColor
        ? p.theme.colors.foreground_lowest
        : p.theme.colors.foreground_lower};
    li:not(:last-of-type) {
      margin-bottom: 20px;
    }
  }
  span {
    font-size: 2.1rem;
    font-weight: 200;
    margin: 0 0 30px;
    display: block;
    color: ${(p) =>
      p.$accentColor ? p.theme.colors.accent : p.theme.colors.foreground_low};
  }
  @media (max-width: 950px) {
    span {
      font-size: 2rem;
    }
  }
  @media (max-width: 800px) {
    grid-column-end: span 1;
    span {
      margin-bottom: 20px;
      margin-top: 20px;
    }
  }
  @media (max-width: 600px) {
    ul {
      font-size: 1.6rem;
    }
  }
  a {
    ${BorderLink({ showLink: false })}
  }
`;

const Colophon = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-column: 2 / span 11;
  margin-top: 10%;
  font-size: 1.6rem;

  p {
    color: ${(p) => p.theme.colors.accent};
  }
  > div:nth-of-type(3) a {
    position: relative;
    padding-left: 33px;
  }
  img {
    pointer-events: none;
    width: 30px;
    height: 30px;
    position: absolute;
    top: 0;
    background-color: ${(p) => p.theme.colors.background};
    padding-right: 8px;
    padding-bottom: 5px;
    left: 0;
  }
  &:hover {
    background-color: ${(p) => p.theme.colors.background};
  }
  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
    grid-column: 2 / span 10;
    > div:nth-of-type(3) {
      justify-content: flex-end;
      display: flex;
    }
    > div:nth-of-type(2) {
      text-align: center;
    }
  }
  @media (max-width: 950px) {
    font-size: 1.6rem;
    img {
      width: 25px;
      height: 30px;
      padding-right: 5px;
    }
    > div:nth-of-type(3) a {
      padding-left: 25px;
    }
  }
  @media (max-width: 800px) {
    grid-column: 1 / span 12;
    flex-direction: column-reverse;
    display: flex;
    margin-top: 50px;
    > div {
      &:nth-of-type(3) {
        justify-content: flex-start;
        margin-bottom: 15px;
      }
      &:nth-of-type(2) {
        text-align: left;
        margin-bottom: 20px;
      }
    }
  }
  div {
    a {
      justify-self: start;
      ${BorderLink({ showLink: true })}
    }
    background-image: none;
  }
`;
